import type { FC } from 'react';
import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Typography } from '@interstate/components/Typography';
import { Box } from '@interstate/components/Box';
import { Grid } from '@interstate/components/Grid';
import type { ComboBoxOptions } from '@interstate/components/ComboBox';
import { ComboBox } from '@interstate/components/ComboBox';
import { Interstitial } from '@interstate/components/Interstitial';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import {
    changeVehicleSelectors,
    fetchCatalogYears,
    fetchCatalogMakes,
    fetchCatalogModels,
    fetchCatalogTrims
} from '@makemydeal/dr-activities-change-vehicle';
import type { ConfigureVehiclesProps } from './ConfigureVehicleProps';
import { useVehicleSelectChange } from './hooks/useVehicleSelectChange';
import { commonSelectFieldProps, useResponsiveGrid } from './styles';
import { useComboBoxHandlers } from './hooks/useComboBoxHandlers';
import { TEST_IDS } from '../../constants';
import { CenteredLoadingIndicator } from '../../../../common/Loading.style';
import {
    getYearTypeOptions,
    getMakeTypeOptions,
    getModelTypeOptions,
    getTrimTypeOptions,
    BuildVehicleSelects,
    shouldEnableDropdown,
    autoCorrectInvalidSelections,
    getYearForApi,
    hasValue,
    hasValidValue,
    inventoryOptions,
    ALL_OPTION
} from './utils';

export const ConfigureVehiclesDynamic: FC<ConfigureVehiclesProps> = ({
    inventoryTypes,
    setInventoryTypes,
    makes,
    setMakes,
    models,
    setModels,
    trims,
    setTrims,
    years,
    setYears
}) => {
    const dispatch = useDispatch();
    const bffEndpoint = useSelector(changeVehicleSelectors.additionalSelectors.getServicesBff);
    const isFetching = useSelector(changeVehicleSelectors.getIsFetching);

    const theme = useInterstateTheme();
    const gridContainerSx = useResponsiveGrid(theme);

    const yearOptions = useSelector(changeVehicleSelectors.getCatalogYears);
    const makeOptions = useSelector(changeVehicleSelectors.getCatalogMakes);
    const modelOptions = useSelector(changeVehicleSelectors.getCatalogModels);
    const trimOptions = useSelector(changeVehicleSelectors.getCatalogTrims);

    useEffect(() => {
        dispatch(fetchCatalogYears(bffEndpoint));
    }, [dispatch, bffEndpoint]);

    useEffect(() => {
        if (hasValue(years)) {
            dispatch(fetchCatalogMakes(bffEndpoint, getYearForApi(years[0])));
        }
    }, [years, dispatch, bffEndpoint]);

    useEffect(() => {
        if (hasValue(years) && hasValidValue(makes)) {
            dispatch(fetchCatalogModels(bffEndpoint, getYearForApi(years[0]), makes[0]));
        }
    }, [years, makes, dispatch, bffEndpoint]);

    useEffect(() => {
        if (hasValue(years) && hasValidValue(makes) && hasValidValue(models)) {
            dispatch(fetchCatalogTrims(bffEndpoint, getYearForApi(years[0]), makes[0], models[0]));
        }
    }, [years, makes, models, dispatch, bffEndpoint]);

    // Helper: enable dropdown only if all previous dropdowns are filled
    const isDropdownEnabled = shouldEnableDropdown({ inventoryTypes, years, makes, models, trims });

    const yearTypeOptions: ComboBoxOptions = getYearTypeOptions(yearOptions);
    const makeTypeOptions: ComboBoxOptions = getMakeTypeOptions(makeOptions);
    const modelTypeOptions: ComboBoxOptions = getModelTypeOptions(modelOptions);
    const trimTypeOptions: ComboBoxOptions = getTrimTypeOptions(trimOptions);

    useEffect(() => {
        if (!isFetching) {
            autoCorrectInvalidSelections(makes, makeTypeOptions, setMakes);
            autoCorrectInvalidSelections(models, modelTypeOptions, setModels);
            autoCorrectInvalidSelections(trims, trimTypeOptions, setTrims);
        }
    }, [makes, makeTypeOptions, setMakes, models, modelTypeOptions, setModels, trims, trimTypeOptions, setTrims, isFetching]);

    const handleSelectChange = useVehicleSelectChange({
        setInventoryTypes,
        setYears,
        setMakes,
        setModels,
        setTrims
    });

    const { createAutoSelectAllOnBlur, createMultiSelectChange } = useComboBoxHandlers({
        handleSelectChange,
        inventoryTypes
    });

    return (
        <Box data-testid={TEST_IDS.CONFIGURE_DYNAMIC_VEHICLES_SECTION}>
            {isFetching && (
                <CenteredLoadingIndicator $hideOverlay data-testid="template-loading-indicator">
                    <Interstitial />
                </CenteredLoadingIndicator>
            )}
            <Typography variant="label-sm" sx={({ tokens }) => ({ color: tokens.ComponentTextInputLabelColor })}>
                Step 2
            </Typography>
            <Typography variant="h5" sx={{ mb: 2 }}>
                Configure Vehicles
            </Typography>
            <Grid sx={gridContainerSx}>
                <ComboBox
                    {...commonSelectFieldProps}
                    label="Inventory Type"
                    multiple
                    maxListBoxHeight={150}
                    value={inventoryOptions.filter((opt) => inventoryTypes.includes(opt.value))}
                    options={inventoryOptions}
                    onChange={createMultiSelectChange(BuildVehicleSelects.inventoryTypes, inventoryOptions)}
                    onBlur={createAutoSelectAllOnBlur(BuildVehicleSelects.inventoryTypes, inventoryOptions)}
                    data-testid={TEST_IDS.TEMPLATE_INVENTORY_TYPE_SELECT}
                />
                <ComboBox
                    {...commonSelectFieldProps}
                    label="Year"
                    maxListBoxHeight={150}
                    value={yearTypeOptions.filter((opt) => years.includes(opt.value))}
                    options={yearTypeOptions}
                    onChange={(e) => {
                        const selected = e.target.value as ComboBoxOptions;
                        handleSelectChange(BuildVehicleSelects.years, [selected[0].value]);
                    }}
                    data-testid={TEST_IDS.TEMPLATE_YEAR_SELECT}
                />
                <ComboBox
                    {...commonSelectFieldProps}
                    label="Make"
                    multiple
                    maxListBoxHeight={150}
                    value={makeTypeOptions.filter((opt) => makes.includes(opt.value))}
                    options={makeTypeOptions}
                    disabled={!isDropdownEnabled(BuildVehicleSelects.makes)}
                    onChange={createMultiSelectChange(BuildVehicleSelects.makes, makeTypeOptions)}
                    onBlur={createAutoSelectAllOnBlur(BuildVehicleSelects.makes, makeTypeOptions)}
                    data-testid={TEST_IDS.TEMPLATE_MAKE_SELECT}
                />
                <ComboBox
                    {...commonSelectFieldProps}
                    multiple
                    label="Model"
                    maxListBoxHeight={150}
                    value={modelTypeOptions.filter((opt) => models.includes(opt.value))}
                    options={modelTypeOptions}
                    disabled={!isDropdownEnabled(BuildVehicleSelects.models)}
                    onChange={createMultiSelectChange(BuildVehicleSelects.models, modelTypeOptions)}
                    onBlur={createAutoSelectAllOnBlur(BuildVehicleSelects.models, modelTypeOptions)}
                    data-testid={TEST_IDS.TEMPLATE_MODEL_SELECT}
                />
                <ComboBox
                    {...commonSelectFieldProps}
                    label="Trim"
                    multiple
                    maxListBoxHeight={150}
                    value={trimTypeOptions.filter((opt) => trims.includes(opt.value))}
                    options={trimTypeOptions}
                    disabled={!isDropdownEnabled(BuildVehicleSelects.trims)}
                    onChange={createMultiSelectChange(BuildVehicleSelects.trims, trimTypeOptions)}
                    onBlur={createAutoSelectAllOnBlur(BuildVehicleSelects.trims, trimTypeOptions)}
                    data-testid={TEST_IDS.TEMPLATE_TRIM_SELECT}
                />
                <ComboBox
                    {...commonSelectFieldProps}
                    multiple
                    label="Configuration"
                    maxListBoxHeight={150}
                    // Configurations input should be disabled now. It will be handled in scope of a separate feature
                    value={[ALL_OPTION]}
                    options={[ALL_OPTION]}
                    disabled={true}
                    data-testid={TEST_IDS.TEMPLATE_CONFIGURATION_SELECT}
                />
            </Grid>
        </Box>
    );
};
